import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { CustomButton } from '@medtronic/pecc-react-component-library-js';
import { Box, Modal } from '@mui/material';
import { omit } from 'lodash';
import { WorkbenchCreateQuery } from '~/api/v2/endpoints/workbench';
import { useCreateWorkbenchMutation, useGetProjectQuery } from '~/api/v2/hooks';
import { ModalHeader } from '~/components';
import { modalActionBlockStyles, modalContainerStyle } from '~/components/styles';
import { useAppDispatch } from '~/redux';
import { closeModalById } from '~/redux/slices/modalSlice';
import { workbenchSchema } from '~/validation';
import { CreateWorkbenchForm } from '.';

const CreateWorkbenchModal = ({ id }: { id: string }) => {
  const projectId = +useParams().projectid!;
  const dispatch = useAppDispatch();
  const { data } = useGetProjectQuery({ projectId });
  const [createWorkbench] = useCreateWorkbenchMutation({
    fixedCacheKey: `create-workbench-project-${projectId}`,
  });

  const formMethods = useForm({
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: yupResolver(workbenchSchema),
    defaultValues: {
      name: '',
      description: '',
      instance_type: '',
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { isValid, isDirty },
  } = formMethods;

  const handleClose = () => {
    reset();
    dispatch(closeModalById(id));
  };

  const onSubmit = (data: WorkbenchCreateQuery) => {
    handleClose();
    createWorkbench({ ...omit(data, 'workbenchGroup'), projectId });
  };

  return (
    <Modal open onClose={handleClose}>
      <Box tabIndex={-1} sx={modalContainerStyle}>
        <ModalHeader
          onClose={handleClose}
          title={['Create New Workbench', `New workbench for project “${data?.name}“`]}
        />
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <CreateWorkbenchForm />
            <Box sx={modalActionBlockStyles}>
              <CustomButton buttonType="borderless" label="Cancel" onClick={handleClose} />
              <CustomButton label="Create" type="submit" disabled={!(isValid && isDirty)} />
            </Box>
          </form>
        </FormProvider>
      </Box>
    </Modal>
  );
};

export default CreateWorkbenchModal;
